/**
 * An intake's return from a payment page, as plain functions of the address bar (no server module, no framework).
 *
 * A paid return (`?session_id=`) is money not yet turned into a model until the start-training action answers
 * `queued`. Until then the session id stays in the address bar, next to the address that reopens the intake, so a
 * reload lands back on the intake with the session and Start runs on it again — the server records a payment once and
 * binds a purchase once. Only the confirmed Start cleans it away. A return without payment (`?checkout=`) carries no
 * money: it is read once and cleaned at once.
 */

export const RETURN_SESSION_PARAM = 'session_id'
export const RETURN_CHECKOUT_PARAM = 'checkout'

export interface CheckoutReturn<Outcome extends string> {
    /** The paid Checkout Session the buyer came back from. */
    sessionId: string | null
    /** What the return without a new payment was. */
    outcome: Outcome | null
    /** The line it leaves. */
    notice: string | null
    /** The buyer left Checkout (the processor's cancel). */
    cancelled: boolean
}

/** Reads a return from the address bar; `notices` names each outcome the product understands and the line it leaves. */
export function readCheckoutReturn<Outcome extends string>(
    search: string,
    notices: Record<Outcome, string>,
): CheckoutReturn<Outcome> {
    const params = new URLSearchParams(search)
    const checkout = params.get(RETURN_CHECKOUT_PARAM)
    const outcome = checkout && Object.prototype.hasOwnProperty.call(notices, checkout) ? (checkout as Outcome) : null
    return {
        sessionId: params.get(RETURN_SESSION_PARAM) || null,
        outcome,
        notice: outcome ? notices[outcome] : null,
        cancelled: outcome === 'cancelled',
    }
}

/** What the start-training action says of the purchase it consumed. */
export interface StartPayment {
    amount: number
    currency: string
    transactionId: string
}

/**
 * GA4's `purchase` params for a queued Start, or null when no money changed hands (a grandfathered model carries a
 * purchase row of amount 0). The action answers `payment` once, on the Start that consumes the purchase; the intake
 * fires on that answer only.
 */
export function purchaseEvent(payment: StartPayment): {transaction_id: string; value: number; currency: string} | null {
    if (payment.amount <= 0) return null
    return {transaction_id: payment.transactionId, value: payment.amount / 100, currency: payment.currency.toUpperCase()}
}

/**
 * `purchaseEvent`, at most once per payment in this browser (`keyPrefix` names the product's storage keys). A
 * purchase released by a failed job is consumed again by the next Start, which answers `payment` again with the same
 * transaction id: that second answer is the same money. Where storage is unavailable the event fires (GA4 may still
 * drop the repeated transaction id).
 */
export function purchaseEventOnce(
    payment: StartPayment,
    storage: Pick<Storage, 'getItem' | 'setItem'> | null,
    keyPrefix: string,
): ReturnType<typeof purchaseEvent> {
    const event = purchaseEvent(payment)
    if (!event) return null
    const key = `${keyPrefix}:${event.transaction_id}`
    try {
        if (storage?.getItem(key)) return null
        storage?.setItem(key, '1')
    } catch {
        // Storage can throw (private window, blocked site data): fire without the memory.
    }
    return event
}

/**
 * Whether the checkout form's submit may leave now, once: false when this page already submitted it (a second press
 * while the browser is still on its way to the payment page: the submit is cancelled, nothing is sent twice).
 * `submitted` is the page's own memory, reset when the browser restores the page from its back-forward cache.
 */
export function submitOnce(submitted: {current: boolean}): boolean {
    if (submitted.current) return false
    submitted.current = true
    return true
}

/** `pathname + search` with the named parameters removed. */
export function withoutParams(pathname: string, search: string, names: string[]): string {
    const params = new URLSearchParams(search)
    for (const name of names) params.delete(name)
    const qs = params.toString()
    return pathname + (qs ? `?${qs}` : '')
}

/** `address` with one query parameter set. */
export function withParam(address: string, name: string, value: string): string {
    const [pathname, search = ''] = address.split('?')
    const params = new URLSearchParams(search)
    params.set(name, value)
    return `${pathname}?${params.toString()}`
}

/** The address a reload must find while a paid session is not yet a model: the intake's reopen address, carrying the
 *  session id. */
export function pendingAddress(reopenAddress: string, sessionId: string): string {
    return withParam(reopenAddress, RETURN_SESSION_PARAM, sessionId)
}

/**
 * The sign-in return carries the ticked box. The intake's consent is the server's to require on every Start and is not
 * the ToS, so it is never kept as a browser flag; it rides this one address instead, through Google (same tab) or a
 * magic link (any tab), and is spent when the intake reads it back.
 */
export const RETURN_CONSENT_PARAM = 'intake_consent'

/** The address the sign-in returns to once the box is ticked. */
export function consentReturnAddress(reopenAddress: string): string {
    return withParam(reopenAddress, RETURN_CONSENT_PARAM, '1')
}

/** Whether the address bar carries the tick of a sign-in return. */
export function readConsentReturn(search: string): boolean {
    return new URLSearchParams(search).get(RETURN_CONSENT_PARAM) === '1'
}

/** Whether the return's Start runs now: once per mount, and only with everything it needs. */
export function shouldAutoStart(state: {
    sessionId: string | null
    alreadyStarted: boolean
    isAuthenticated: boolean
    isReady: boolean
    verifying: boolean
    idle: boolean
}): boolean {
    return (
        !!state.sessionId &&
        !state.alreadyStarted &&
        state.isAuthenticated &&
        state.isReady &&
        !state.verifying &&
        state.idle
    )
}
